import { Icon, type IconName } from '@d-edge/overnight-hotelier-react'
import type { SVGProps } from 'react'
import { SpiderIcon } from './SpiderIcon'

export interface IconProps {
  /** Pixel size; mapped onto the kit's small / medium / large scale. */
  size?: number
  className?: string
  'aria-label'?: string
  /** Accepted for backwards compatibility; font icons are always filled. */
  fill?: string
}

function kitSize(size = 16) {
  return size <= 12 ? 'small' : size <= 16 ? 'medium' : 'large'
}

function kit(name: IconName) {
  return ({ size, className, 'aria-label': label }: IconProps) => <Icon name={name} size={kitSize(size)} className={className} label={label} />
}

// Kept as inline SVG because the kit's icon subset has no equivalent.
function svg(path: string) {
  return ({ size = 16, className, 'aria-label': label }: IconProps) => {
    const a11y: SVGProps<SVGSVGElement> = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true }
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} {...a11y}>
        <path d={path} />
      </svg>
    )
  }
}

/** Icon set used across the cockpit; every entry is a kit icon unless noted. */
export const Icons = {
  Refresh: kit('arrow-rotate-right'),
  Close: kit('xmark-large'),
  Search: kit('magnifying-glass'),
  External: kit('arrow-up-right-from-square'),
  Trash: svg('M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3'),
  History: kit('box-archive'),
  Play: kit('circle-play'),
  Edit: kit('pen-to-square'),
  Plus: kit('plus'),
  ChevronDown: kit('chevron-down'),
  ChevronRight: kit('chevron-right'),
  Check: kit('check'),
  CheckCircle: kit('circle-check'),
  Alert: kit('triangle-exclamation'),
  Stop: kit('octagon-exclamation'),
  Info: kit('circle-info'),
  Star: kit('star'),
  Copy: kit('copy'),
  Calendar: kit('calendar-day'),
  Sync: kit('arrow-rotate-right'),
  Menu: kit('bars-sort'),
  ArrowUp: kit('arrow-up'),
  ArrowDown: kit('arrow-down'),
  ArrowRight: kit('arrow-right'),
  Question: kit('question'),
  Sun: svg('M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4'),
  Moon: svg('M21 13A9 9 0 1 1 11 3a7 7 0 0 0 10 10z'),
  Grid: kit('grid'),
  Settings: kit('gears'),
  SignOut: kit('arrow-right-from-bracket'),
  Spider: SpiderIcon,
}
